<template>
	<section class="course insicht insicht--fade-up">
		<div class="course__list">
			<h2
				class="course__title"
				v-html="$t(`course.title.${courseType}`)"
			/>
			<div
				class="course__row"
				v-for="location in enhancedCourseDates"
				:key="`courses-${location.brand}-${location.location}`"
			>
				<h3
					class="course__row-title"
					v-html="`${location.location}, ${$t(`brands.${location.brand}`)}`"
				/>

				<div class="course__row-items">
					<div
						class="course__item"
						v-for="course in location.courses"
						:key="course.id"
					>
						<input
							class="course__item-input"
							type="radio"
							name="courseId"
							:checked="course.id == form.courseId"
							:value="course.id"
							:id="`course-${course.id}`"
							:disabled="loading"
							@change="onSelectDate(course)"
						/>
						<label
							class="course__item-label"
							:for="`course-${course.id}`"
						>
							<span
								class="course__item-date"
								v-html="formatDate(course.date)"
							/>
							<span
								class="course__item-time"
								v-html="formatTime(course)"
							/>
							<span
								class="course__item-lang"
								v-html="$t(`course.language.${course.locale}`)"
							/>
							<span
								v-if="!isEvent"
								class="course__item-free"
								v-html="$t('course.freePlaces', course)"
							/>
							<span
								v-if="course.price"
								class="course__item-price"
								v-html="$options.filters.fwgpricecent(course.price, 'html')"
							/>
							<span
								v-else
								class="course__item-price"
								v-html="$t('course.free')"
							/>
						</label>
					</div>
				</div>

			</div>
			<div
				class="course__row"
				v-if="!enhancedCourseDates.length"
			>
				<h3
					v-if="!isEvent"
					class="course__row-title"
					v-html="$t('course.noDates')"
				/>
				<h3
					v-else
					class="course__row-title"
					v-html="$t('course.noDatesEvent')"
				/>
			</div>
		</div>

		<div
			class="checkout-form__container"
			v-if="form.courseId"
		>
			<h2
				class="course__title"
				v-html="$t('course.formTitle')"
			/>

			<GeneralMessage
				ref="message"
				v-if="response.message"
				:message="response.message"
				:type="response.type"
				stick
			/>

			<ValidationObserver
				ref="form"
				v-slot="{ invalid, errors }"
				tag="form"
				class="checkout-form__form-wrapper"
				novalidate
				@submit.prevent="onSubmit"
			>

				<div class="input-row">
					<FormInputSelect
						v-model="form.participants"
						:options="participants"
						:label="$t('course.participantsLabel')"
						:disabled="loading"
					/>
				</div>

				<ValidationObserver
					tag="section"
					ref="address"
					vid="address"
					v-slot="{ invalid }"
				>
					<div class="input-row">
						<ValidationProvider
							class="input-row__wrapper"
							name="firstname"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.firstname')"
								:label="$t('forms.labels.firstname')"
								name="firstname"
								v-model="form.firstName"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
						</ValidationProvider>
						<ValidationProvider
							class="input-row__wrapper"
							name="lastname"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.lastname')"
								:label="$t('forms.labels.lastname')"
								name="lastname"
								v-model="form.lastName"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
						</ValidationProvider>
					</div>

					<div class="input-row">
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.company')"
							:label="$t('forms.labels.company')"
							:labelOption="$t('forms.labels.optional')"
							name="company"
							v-model="form.company"
							:required="false"
							:disabled="loading"
						/>
					</div>

					<div class="input-row">

						<ValidationObserver
							class="input-row__wrapper"
							tag="div"
							v-slot="{ validated, invalid, errors }"
						>
							<div class="input-group">
								<ValidationProvider
									class="input-group__span-9"
									name="street"
									rules="required"
									v-slot="{ errors, invalid }"
									tag="div"
									:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
								>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.street')"
										:label="$t('forms.labels.street')"
										name="street"
										v-model="form.street"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
								<ValidationProvider
										class="input-group__span-3"
										name="streetNumber"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.number')"
										:label="$t('forms.labels.number')"
										name="number"
										v-model="form.streetNumber"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
							</div>
							<div
								class="input-error input-group__span-12"
								v-if="invalid && validated"
								v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
							/>
						</ValidationObserver>

						<ValidationObserver
							class="input-row__wrapper"
							tag="div"
							ref="zipCity"
							v-slot="{ dirty, validated, invalid, errors }"
						>
							<div class="input-group">
								<ValidationProvider
									class="input-group__span-4 input-group__span-xxl-3"
									name="zip"
									rules="required|digits:4"
									v-slot="{ errors, invalid }"
									tag="div"
									:customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
								>
									<FormInput
										ref="courseZip"
										type="text"
										@keydown="onZipKeydown"
										@input="onZipInput"
										:placeholder="$t('forms.labels.zip')"
										:label="$t('forms.labels.zip')"
										name="zip"
										v-model="form.postcode"
										:minlength="4"
										:maxlength="4"
										inputmode="decimal"
										pattern="[0-9]{4}"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
								<ValidationProvider
										class="input-group__span-8 input-group__span-xxl-9"
										name="city"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.city')"
										:label="$t('forms.labels.city')"
										name="city"
										v-model="form.city"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
							</div>
							<div
								class="input-error input-group__span-12"
								v-if="invalid && (validated || dirty)"
								v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
							/>
						</ValidationObserver>
					</div>

					<div class="input-row">
						<ValidationProvider
							class="input-row__wrapper"
							name="phone"
							rules="required|phone"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
						>
							<FormInput
								type="tel"
								:placeholder="$t('forms.labels.phone')"
								:label="$t('forms.labels.phone')"
								name="phone"
								v-model="form.phoneNumber"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
							<span class="additional-info" v-html="$t('course.phoneHint')"/>
						</ValidationProvider>

						<ValidationProvider
							class="input-row__wrapper"
							name="email"
							rules="required|email"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
						>
							<FormInput
								type="email"
								name="email"
								autocomplete="email"
								:placeholder="$t('forms.labels.email')"
								:label="$t('forms.labels.email')"
								v-model="form.email"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-html="errors[0]"
							/>
							<span class="additional-info" v-html="$t('course.emailHint')"/>
						</ValidationProvider>
					</div>

				</ValidationObserver>


				<div class="input-row">
					<div class="input-row__wrapper">
						<FormInputTextarea
							:placeholder="$t('forms.labels.notes')"
							:label="$t('forms.labels.notes')"
							:labelOption="$t('forms.labels.optional')"
							v-model="form.notes"
							:disabled="loading"
						/>
					</div>
				</div>

				<template
					v-if="form.participants > 1"
				>
					<h3
						class="course__subtitle"
						v-html="$t('course.participantsTitle')"
					/>
					<template
						v-for="participant in form.participants - 1"
					>
						<label
							class="input-row-label"
							v-html="$t('course.participantRowLabel', { number: participant })"
						/>
						<div
							class="input-row"
						>
							<ValidationProvider
								class="input-row__wrapper"
								:name="`firstname-${participant}`"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.firstname')"
									:label="$t('forms.labels.firstname')"
									name="firstname"
									v-model="participantFirstNames[participant - 1]"
									:error="errors.length > 0"
									:disabled="loading"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>
							<ValidationProvider
								class="input-row__wrapper"
								:name="`lastname-${participant}`"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.lastname')"
									:label="$t('forms.labels.lastname')"
									name="lastname"
									v-model="participantLastNames[participant - 1]"
									:error="errors.length > 0"
									:disabled="loading"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>
						</div>
					</template>
				</template>

				<FormInput
					ref="captcha"
					type="text"
					:placeholder="$t('forms.labels.addressDetails')"
					:label="$t('forms.labels.addressDetails')"
					:name="captcha.id"
					:id="captcha.id"
					v-model="captcha.value"
					required
					v-show="false"
				/>

				<ValidationProvider
					v-if="courseTermsPage"
					:rules="{ required: { allowFalse: false } }"
					name="checkTerms"
					tag="div"
					v-slot="{ invalid, errors, validated }"
					:customMessages="{ required: $t('course.errorCancellationTerms') }"
				>
					<FormInputCheckbox
						name="terms"
						value="checkTerms"
						v-model="form.checkTerms"
						required
						immediate
					>
						<span v-html="$t('course.cancellationTermsLabel', { url: courseTermsPage })"/>
					</FormInputCheckbox>
					<span
						class="input-error"
						v-if="invalid && validated"
						v-html="errors[0]"
					/>
				</ValidationProvider>

				<Button
					type="submit"
					level="primary"
					:text="$t('course.submitButton')"
					:disabled="loading"
				/>
			</ValidationObserver>
		</div>
	</section>
</template>

<script>
import {mapGetters} from 'vuex'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputRadio from '@/components/FormInputRadio'
import veevalidate from "@/helpers/veevalidate"
import urlWatcher from '@/helpers/urlwatcher'
import GenericMixin from '@/mixins/GenericVueMixin'
import Button from '@/components/Button'
import Loader from '@/components/Loader'
import GeneralMessage from '@/components/GeneralMessage'
import leadingZero from '@/utils/leadingZero'
import sleep from '@/utils/sleep'
import { format } from '@/utils/format'
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"
import { contaoService } from '@/service'
import scrrroll from 'scrrroll';


export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		FormInputRadio,
		Button,
		Loader,
		GeneralMessage,
	},
	data() {
		return {
			captcha: {
				id: this.captchaId(),
				value: '',
			},
			form: {
				courseId: '',
				participants: 1,
				company: '',
				firstName: '',
				lastName: '',
				street: '',
				streetNumber: '',
				postcode: '',
				city: '',
				phoneNumber: '',
				email: '',
				participantNames: '',
				notes: '',
				checkTerms: false,
			},
			participantFirstNames: [],
			participantLastNames: [],
			response: {
				message: '',
				type: '',
			},
			courseType: this.$root.options.courseType,
			courseTermsPage: this.$root.options.courseTermsPage,
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters('courses', [
			'courseDates',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		enhancedCourseDates() {
			return this.courseDates
				.map(location => {
					return {
						...location,
						courses: location.courses
							.filter(course => course.free)
							.sort((a, b) => a.date.localeCompare(b.date) || a.fromTime.localeCompare(b.fromTime))
					}
				})
				.filter(location => location.courses.length)
				.sort((a, b) => a.location.localeCompare(b.location))
		},
		courses() {
			return this.courseDates.reduce((a, l) => {
				return [
					...a,
					...l.courses.map(c => {
						return {
							...c,
							brand: l.brand,
							location: l.location,
						}
					})
				]
			}, []);
		},
		selectedCourse() {
			return this.courses.find(c => c.id == this.form.courseId);
		},
		maximumParticipants() {
			const max = 8;
			const free = this.selectedCourse?.free || max;

			return Math.min(max, free);
		},
		participants() {
			const participants = [];
			for (let i = 1; i <= this.maximumParticipants; i++) {
				participants.push({
					value: i,
					label: i,
				});
			}
			return participants;
		},
		totalText() {
			if (!this.selectedCourse) {
				return '';
			}
			return `${this.form.participants}× ${this.formatDate(this.selectedCourse.date)} ${this.formatTime(this.selectedCourse)}`
		},
		totalSubtext() {
			if (!this.selectedCourse) {
				return '';
			}
			return `${this.selectedCourse.location}, ${this.selectedCourse.brand}`
		},
		totalValue() {
			if (!this.selectedCourse) {
				return 0
			}
			return this.form.participants * this.selectedCourse.price
		},
		isEvent() {
			return this.courseType === 'event';
		}
	},
	watch: {
		'form.participants'(val) {
			this.participantFirstNames.splice(val - 1);
			this.participantLastNames.splice(val - 1);
		}
	},
	methods: {
		rowTitle(location, start, stop) {
			return `${location}, jeweils von ${start} - ${stop}`;
		},
		freePlaces(number) {
			return `(${number} freie Plätze)`;
		},
		formatDate(date) {
			return dayjs(date).format('dd, DD.MM.YYYY');
		},
		formatTime(course) {
			const timeString = `${course.timeStart || course.fromTime} - ${course.timeStop || course.toTime}`;
			return this.$te(timeString) ? this.$t(timeString) : timeString;
		},
		onSelectDate(course) {
			this.form.participants = Math.min(course.free, this.form.participants)
			this.form.courseId = course.id;
		},
		onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.courseZip.$refs.input.value = this.form.postcode;
			}
		},
		async onSubmit() {
			this.loading = true;
			const valid = await this.$refs.form.validate();

			if (this.captcha.value || this.$refs.captcha.$refs.input.value) {
				this.loading = false;
				return;
			}

			if (valid) {
				if (this.form.participants > 1) {
					this.form.participantNames = this.participantFirstNames
						.reduce((names, firstName, index) => {
							const lastName = this.participantLastNames[index];
							const name = [];

							if (firstName) {
								name.push(firstName);
							}

							if (lastName) {
								name.push(lastName);
							}

							if (name.length) {
								names.push(name.join(' '));
							}


							return names;
						}, [])
					.join(', ');
				}

				try {
					const response = await this.$store.dispatch('courses/courseRegistration', {...this.form});
				}
				catch(err) {
					if (err.response && err.response.data.message) {
						this.response.message = err.response.data.errors && Object.values(err.response.data.errors).length
							? Object.values(err.response.data.errors).flat().join()
							: err.response.data.message
					} else {
						this.response.message = this.$t('forms.errors.generic');
					}
					this.response.type = 'error';
					this.$nextTick(() => {
						this.$refs.message.scrollTo();
					});
					this.loading = false;
				}

			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
		resetForm() {
			this.$refs.form.reset();
			this.form.participants = 1;
			this.form.company = '';
			this.form.firstName = '';
			this.form.lastName = '';
			this.form.street = '';
			this.form.streetNumber = '';
			this.form.postcode = '';
			this.form.city = '';
			this.form.phoneNumber = '';
			this.form.email = '';
			this.form.participantNames = '';
			this.form.notes = '';
			this.participantFirstNames = [];
			this.participantLastNames = [];
			this.captcha.id = this.captchaId();
			this.captcha.value = '';
		},
		captchaId() {
			return `cf-${(Date.now().toString(36) + Math.random().toString(36).substring(2, 7))}`;
		}
	},
	async created() {
		this.loading = true;
		try {
			await this.$store.dispatch('courses/loadCourseDates', { courseType: this.courseType });
		}
		catch(error) {}
		this.loading = false;
	}
};
</script>

<style>
</style>
